import React, { useState } from 'react';
import { Clock, Search, X, MapPin } from 'lucide-react';

/**
 * Компонент для выбора часового пояса с поиском
 */
const TimezoneSelector = ({ timezone, setTimezone, timezones }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Фильтрация часовых поясов по поисковому запросу
  const filteredTimezones = searchQuery.trim()
    ? timezones.filter(tz => 
        tz.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tz.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tz.offset.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : timezones;

  // Получение названия текущего часового пояса
  const currentTimezone = timezones.find(tz => tz.id === timezone) || { name: timezone, offset: '' };

  // Обработчик выбора часового пояса
  const handleSelectTimezone = (id) => {
    setTimezone(id);
    setIsOpen(false);
    setSearchQuery('');
  };

  return (
    <div className="mb-6 relative">
      <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
        <Clock className="w-4 h-4 mr-2 text-cyan-500" />
        Часовой пояс
      </label>
      
      {/* Кнопка выбора часового пояса */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md p-2 text-sm text-left focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors"
      >
        <div className="flex items-center">
          <MapPin className="w-4 h-4 mr-2 text-gray-500 dark:text-gray-400" />
          <span>{currentTimezone.name}</span>
          <span className="ml-2 text-gray-500 dark:text-gray-400 text-xs">{currentTimezone.offset}</span>
        </div>
        <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
      
      {/* Выпадающий список часовых поясов */}
      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 shadow-lg max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm border border-gray-200 dark:border-gray-700">
          <div className="sticky top-0 p-2 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 placeholder-gray-500 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm transition-colors"
                placeholder="Поиск часового пояса..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setSearchQuery('')}
                >
                  <X className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                </button>
              )}
            </div>
          </div>
          
          <ul className="mt-1">
            {filteredTimezones.length > 0 ? (
              filteredTimezones.map((tz) => (
                <li
                  key={tz.id}
                  className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                    timezone === tz.id ? 'bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300' : 'text-gray-900 dark:text-gray-200'
                  }`}
                  onClick={() => handleSelectTimezone(tz.id)}
                >
                  <div className="flex items-center">
                    <span className="font-normal block truncate">{tz.name}</span>
                    <span className="ml-2 text-gray-500 dark:text-gray-400 text-xs">{tz.offset}</span>
                  </div>
                  
                  {timezone === tz.id && (
                    <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-cyan-600 dark:text-cyan-400">
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </span>
                  )}
                </li>
              ))
            ) : (
              <li className="py-3 px-3 text-gray-500 dark:text-gray-400 text-center">
                Не найдено часовых поясов
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default TimezoneSelector;